import type {
  DomainEventDefinition,
  JsonValue,
  RecordDefinition,
  Resource,
  Snapshot,
  Transaction,
} from "@e308/core";
import {
  type CapturedSettlement,
  runCombatStep,
  settleBorderShields,
} from "./combat-simulation.js";
import { UNITS } from "./content/buildings.js";
import type { EconomySettings, SeatModel } from "./content-types.js";
import {
  cleanupDiplomacyStep,
  resolveIncidentStep,
  resolvePactStep,
  resolveShipmentStep,
} from "./diplomacy-runtime.js";
import type { DiplomacyState, MatchLogPayload } from "./diplomacy-types.js";
import { settleEconomy } from "./economy.js";
import {
  ECONOMY_BUILDINGS,
  type EconomyBuilding,
  type EconomyInput,
  type EconomySettlement,
} from "./economy-types.js";
import { recordDamageSummaries, recordProductionMilestones } from "./event-runtime.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { type LifecycleEvents, runLifecycleCleanup, runOvertimeStep } from "./lifecycle-runtime.js";
import type { LockedMatchSetup } from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { runAutoReinforceStep, runTransitStep } from "./logistics-simulation.js";
import { advanceQueueHead } from "./manufacturing-state.js";
import type { ManufacturingState, QueueKind } from "./manufacturing-types.js";
import { routeProduction } from "./reinforcement-runtime.js";
import { effectsForResearch } from "./research-effects.js";
import { runResearchStep } from "./research-simulation.js";
import type { ResearchState } from "./research-types.js";
import { buildingResource, unitResource } from "./seat-resources.js";
import type { CanonicalPhase, EconomyProjection, FormattedDiagnostic } from "./types.js";

type SeatResource = Resource<Micro>;

export function runConflictStep(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  elapsedMs: number,
  phase: (phase: CanonicalPhase) => void,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
  diplomacy: RecordDefinition<DiplomacyState & JsonValue>,
  setup: LockedMatchSetup,
  events: LifecycleEvents & {
    readonly shieldBreached: DomainEventDefinition<import("./content-types.js").ShieldEventPayload>;
    readonly matchLog: DomainEventDefinition<MatchLogPayload & JsonValue>;
  },
): void {
  phase("research-treaties");
  resolvePactStep(transaction, diplomacy, events.matchLog);
  runResearchStep(transaction, seats, elapsedMs);
  runTransitStep(transaction, seats, elapsedMs, lifecycle);
  phase("operation");
  const settlements = seats.map((model) => ({
    model,
    result: computeSeat(transaction, model, elapsedMs),
  }));
  phase("construction");
  for (const settlement of settlements)
    applySettlement(transaction, settlement, diplomacy, events.matchLog);
  settleBorderShields(transaction, settlements);
  phase("deployment");
  runAutoReinforceStep(transaction, seats, elapsedMs, lifecycle);
  phase("combat");
  const reports = runCombatStep(
    transaction,
    settlements,
    elapsedMs,
    lifecycle,
    diplomacy,
    setup,
    events.shieldBreached,
  );
  phase("scheduled-events");
  runOvertimeStep(transaction, seats, lifecycle, setup, elapsedMs, events.overtimeStarted);
  recordDamageSummaries(transaction, reports, diplomacy, events.matchLog);
  resolveShipmentStep(transaction, seats, diplomacy, events.matchLog);
  resolveIncidentStep(
    transaction,
    seats,
    lifecycle,
    diplomacy,
    new Map(
      settlements.map(({ model, result }) => [model.seat.id, result.diagnostics.shieldCapacity]),
    ),
    events.matchLog,
  );
  phase("cleanup");
  runLifecycleCleanup(transaction, seats, lifecycle, setup, elapsedMs, events);
  cleanupDiplomacyStep(transaction, seats, lifecycle, diplomacy, events.matchLog);
}

export function observeEconomy(
  snapshot: Snapshot<Micro>,
  model: SeatModel,
): {
  readonly buildings: Readonly<Record<EconomyBuilding, string>>;
  readonly projection: EconomyProjection;
} {
  const input = inputFrom(
    (resource) => snapshot.resources[resource.id] as Micro,
    (id, target) => snapshot.allocations[id]?.[target] as Micro,
    snapshot.records?.[model.economySettings.id]?.value as unknown as EconomySettings,
    snapshot.records?.[model.manufacturing.id]?.value as unknown as ManufacturingState,
    snapshot.records?.[model.research.id]?.value as unknown as ResearchState,
    model,
    1_000,
    snapshot.gameTimeMs,
  );
  const result = settleEconomy(input);
  return Object.freeze({
    buildings: Object.freeze(
      Object.fromEntries(ECONOMY_BUILDINGS.map((id) => [id, formatMicro(input.buildings[id])])),
    ) as Record<EconomyBuilding, string>,
    projection: formatProjection(result.diagnostics),
  });
}

function computeSeat(
  transaction: Transaction<Micro>,
  model: SeatModel,
  elapsedMs: number,
): EconomySettlement {
  const input = inputFrom(
    (resource) => transaction.get(resource),
    (id, target) => transaction.getAllocation(id, target),
    transaction.getRecord(model.economySettings),
    transaction.getRecord(model.manufacturing),
    transaction.getRecord(model.research),
    model,
    elapsedMs,
    transaction.gameTimeMs(),
  );
  return settleEconomy(input);
}

function applySettlement(
  transaction: Transaction<Micro>,
  settlement: CapturedSettlement,
  diplomacy: RecordDefinition<DiplomacyState & JsonValue>,
  matchLog: DomainEventDefinition<MatchLogPayload & JsonValue>,
): void {
  const { model, result } = settlement;
  recordProductionMilestones(transaction, model, result, diplomacy, matchLog);
  transaction.set(model.resources.metal, result.metal);
  transaction.set(model.resources.researchPoints, result.researchPoints);
  for (const id of UNITS.map((entry) => entry.id)) {
    transaction.set(unitResource(model.resources, id), result.units[id]);
  }
  routeNewTroops(transaction, model, result);
  for (const id of ECONOMY_BUILDINGS) {
    transaction.set(buildingResource(model.resources, id), result.buildings[id]);
    transaction.set(model.resources.wreckage[id], result.wreckage[id]);
  }
  let manufacturing = transaction.getRecord(model.manufacturing);
  for (const queue of ["construction", "restoration", "assembly"] as const) {
    manufacturing = advanceQueueHead(manufacturing, queue, progressFor(result, queue));
  }
  transaction.setRecord(model.manufacturing, manufacturing);
}

function routeNewTroops(
  transaction: Transaction<Micro>,
  model: SeatModel,
  result: EconomySettlement,
): void {
  const logistics = transaction.getRecord(
    model.logistics,
  ) as import("./logistics-types.js").LogisticsState;
  if (!logistics.reinforcement) return;
  const unit = transaction.getRecord(model.manufacturing).assembly[0]?.target;
  if (!unit || !UNITS.some((entry) => entry.id === unit)) return;
  const produced = Object.fromEntries(
    UNITS.map((entry) => [
      entry.id,
      formatMicro(entry.id === unit ? result.progress.assembly : (0n as Micro)),
    ]),
  ) as import("./logistics-types.js").TroopComposition;
  const routed = routeProduction(logistics.reinforcement, produced);
  for (const entry of UNITS) {
    const resource = unitResource(model.resources, entry.id);
    transaction.set(
      resource,
      (transaction.get(resource) -
        microFromString(produced[entry.id]) +
        microFromString(routed.home[entry.id])) as Micro,
    );
  }
  transaction.setRecord(model.logistics, {
    ...logistics,
    reinforcement: routed.state,
  } as import("./logistics-types.js").LogisticsState & JsonValue);
}

function inputFrom(
  get: (resource: SeatResource) => Micro,
  allocation: (id: string, target: string) => Micro,
  settings: EconomySettings,
  manufacturing: ManufacturingState,
  research: ResearchState,
  model: SeatModel,
  elapsedMs: number,
  gameTimeMs: number,
): EconomyInput {
  const percent = (id: string, target: string) => Number(allocation(id, target) / 1_000_000n);
  return {
    alive: get(model.resources.headquarters) > 0n,
    elapsedMs,
    metal: get(model.resources.metal),
    researchPoints: get(model.resources.researchPoints),
    units: Object.freeze({
      "guard-robot": get(model.resources.guardRobot),
      "raider-robot": get(model.resources.raiderRobot),
      "siege-gun": get(model.resources.siegeGun),
    }),
    buildings: Object.freeze(
      Object.fromEntries(
        ECONOMY_BUILDINGS.map((id) => [id, get(buildingResource(model.resources, id))]),
      ),
    ) as EconomyInput["buildings"],
    wreckage: Object.freeze(
      Object.fromEntries(ECONOMY_BUILDINGS.map((id) => [id, get(model.resources.wreckage[id])])),
    ) as EconomyInput["wreckage"],
    powerSliders: Object.freeze({
      industry: percent(model.powerOperation.id, "industry"),
      defenses: percent(model.powerOperation.id, "defenses"),
      "military-production": percent(model.powerOperation.id, "military-production"),
      research: percent(model.powerOperation.id, "research"),
    }),
    powerPriority: settings.powerPriority,
    metalPriority: settings.metalPriority,
    factoryWorksSplit: Object.freeze({
      "rig-factory": percent(model.factoryWorksSplit.id, "rig-factory"),
      "yard-factory": percent(model.factoryWorksSplit.id, "yard-factory"),
      "robot-factory": percent(model.factoryWorksSplit.id, "robot-factory"),
    }),
    manufacturing: Object.freeze({
      newConstruction: workItem(manufacturing, "construction"),
      restoration: workItem(manufacturing, "restoration"),
      assembly: workItem(manufacturing, "assembly"),
      constructionSplit: Object.freeze({
        newConstruction: percent(model.constructionSplit.id, "new-construction"),
        restoration: percent(model.constructionSplit.id, "restoration"),
      }),
      restorationMetalRatio: effectsForResearch(research).ratios["restoration-metal"] ?? "0.5",
      restorationWorkRatio: "0.5",
      factoryWorksOperating:
        manufacturing.factoryWorksRetoolUntilMs === 0 ||
        gameTimeMs - elapsedMs >= manufacturing.factoryWorksRetoolUntilMs,
    }),
    researchEffects: effectsForResearch(research),
  };
}

function workItem(state: ManufacturingState, queue: QueueKind) {
  if (state.paused[queue]) return null;
  const head = state[queue][0];
  return head ? { target: head.target as never, remaining: microFromString(head.remaining) } : null;
}

function progressFor(result: EconomySettlement, queue: QueueKind): Micro {
  return queue === "construction"
    ? result.progress.newConstruction
    : queue === "restoration"
      ? result.progress.restoration
      : result.progress.assembly;
}

function formatProjection(
  value: ReturnType<typeof settleEconomy>["diagnostics"],
): EconomyProjection {
  const diagnostics = (
    entries: Readonly<
      Record<
        string,
        {
          status: "operating" | "idle" | "power-limited" | "metal-limited";
          requested: Micro;
          received: Micro;
          explanation: string;
        }
      >
    >,
  ) =>
    Object.freeze(
      Object.fromEntries(
        Object.entries(entries).map(([id, item]) => [
          id,
          Object.freeze({
            ...item,
            requested: formatMicro(item.requested),
            received: formatMicro(item.received),
          }),
        ]),
      ),
    ) as Readonly<Record<string, FormattedDiagnostic>>;
  return Object.freeze({
    availablePower: formatMicro(value.availablePower),
    unusedPower: formatMicro(value.unusedPower),
    grossMetal: formatMicro(value.grossMetal),
    metalSpent: formatMicro(value.metalSpent),
    netMetal: formatMicro(value.netMetal),
    constructionWork: formatMicro(value.constructionWork),
    assemblyWork: formatMicro(value.assemblyWork),
    researchPoints: formatMicro(value.researchPoints),
    shieldRecharge: formatMicro(value.shieldRecharge),
    shieldCapacity: formatMicro(value.shieldCapacity),
    operatedShieldGenerators: formatMicro(value.operatedShieldGenerators),
    supplyCapacity: formatMicro(value.supplyCapacity),
    power: diagnostics(value.power) as EconomyProjection["power"],
    metalLanes: diagnostics(value.metal) as EconomyProjection["metalLanes"],
  });
}
